/**
 * The four weird playable karts: stats + primitive-built meshes.
 *
 * Coordinate conventions (see src/types.ts):
 *   - Y-up world, karts rest on the y = 0 plane.
 *   - Meshes are modelled facing +Z, centered in XZ.
 *   - Rough envelope: ~2 long (Z) x 1.2 wide (X) x <=1.5 tall (Y).
 * Every wheel's lowest point sits at ~y = 0 so the kart rests on the ground.
 *
 * `createVehicle()` returns a VehicleVisual: the group plus the handles the game
 * animates each frame — wheels to spin, front wheels to steer, exhaust anchors
 * for boost flames, rear contact patches for tyre smoke and skid marks, and the
 * emissive materials driven by boosting and braking.
 *
 * Two things keep a full eight-kart grid cheap:
 *
 *   - Parts that share a material are welded into a single geometry, so a kart
 *     is a dozen meshes rather than fifty. The shopping cart's whole wire basket
 *     is one draw call.
 *   - Each vehicle is built once into a prototype and then cloned. Clones share
 *     geometry and materials; only the emissive materials that animate per kart
 *     (boost glow, brake lights) are copied.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

import { VehicleStats, VehicleVisual } from './types';

// ---------------------------------------------------------------------------
// Stats — four clearly distinct silhouettes and trade-offs.
// ---------------------------------------------------------------------------
export const VEHICLES: VehicleStats[] = [
  {
    id: 'duck',
    name: 'Rubber Duck Racer',
    emoji: '🦆',
    description: 'Bouncy, balanced all-rounder. No glaring weakness.',
    color: 0xffd400,
    maxSpeed: 52,
    accel: 30,
    reverseSpeed: 20, // ~ maxSpeed * 0.4
    handling: 2.1,
    grip: 0.8,
    boostMul: 1.5,
  },
  {
    id: 'chair',
    name: 'Rocket Office Chair',
    emoji: '🪑',
    description: 'Light and twitchy: blazing acceleration and razor turns — but not much top speed.',
    color: 0x2f6fed,
    maxSpeed: 48,
    accel: 42,
    reverseSpeed: 19, // ~ maxSpeed * 0.4
    handling: 2.7,
    grip: 0.6,
    boostMul: 1.45,
  },
  {
    id: 'tub',
    name: 'Turbo Bathtub',
    emoji: '🛁',
    description: 'Heavy tank. Slow to spin up, but the highest top speed on the grid.',
    color: 0xf4f6f8,
    maxSpeed: 62,
    accel: 17,
    reverseSpeed: 24, // ~ maxSpeed * 0.4
    handling: 1.6,
    grip: 0.9,
    boostMul: 1.4,
  },
  {
    id: 'cart',
    name: 'Runaway Shopping Cart',
    emoji: '🛒',
    description: 'Feather-light zippy start, but wobbly and easy to slide out.',
    color: 0xb8bfc6,
    maxSpeed: 46,
    accel: 38,
    reverseSpeed: 18, // ~ maxSpeed * 0.4
    handling: 2.5,
    grip: 0.62,
    boostMul: 1.5,
  },
];

const VEHICLE_BY_ID: Map<string, VehicleStats> = new Map(VEHICLES.map((v) => [v.id, v]));

/** Return stats for `id`, falling back to the first vehicle for unknown ids. */
export function getVehicle(id: string): VehicleStats {
  return VEHICLE_BY_ID.get(id) ?? VEHICLES[0];
}

// ---------------------------------------------------------------------------
// Shared materials
// ---------------------------------------------------------------------------

/**
 * Glossy moulded plastic / lacquered paint. The clear coat is what makes the
 * duck read as rubber rather than flat-shaded geometry: it picks up a sharp
 * highlight from the sky IBL on top of the diffuse colour.
 */
function paint(color: number, roughness = 0.42): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color,
    roughness,
    metalness: 0.02,
    clearcoat: 0.75,
    clearcoatRoughness: 0.2,
  });
}

/** Polished metal — chrome taps, wire baskets, chair frames. */
function metal(color: number, roughness = 0.18): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.95 });
}

/** Plain non-reflective stock: rubber, cushion fabric, painted steel. */
function matte(color: number, roughness = 0.8): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.05 });
}

/** A light source that is only ever seen as a bright surface. */
function lamp(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x120a08,
    roughness: 0.35,
    emissive: new THREE.Color(color),
    emissiveIntensity: 0,
  });
}

const TYRE_MAT = matte(0x14141a, 0.92);
const RIM_MAT = metal(0xd6dce4, 0.24);
const CHROME_MAT = metal(0xdfe6ee, 0.12);
const DUCK_BODY = paint(0xffd400, 0.38);
const DUCK_BEAK = paint(0xff8c00, 0.45);
const EYE_WHITE = paint(0xffffff, 0.22);
const EYE_BLACK = matte(0x0b0b10, 0.25);
const CHAIR_FRAME = paint(0x2f6fed, 0.4);
const CHAIR_CUSHION = matte(0x1f3f8c, 0.85);
const CHAIR_ROCKET = metal(0xd0d6dd, 0.25);
const CHAIR_NOSE = paint(0xff5a1f, 0.4);
const PORCELAIN = paint(0xf4f6f8, 0.16);
const PORCELAIN_INNER = paint(0xe4ecf2, 0.3);
const BRASS = metal(0xc9a13b, 0.3);
const WIRE = metal(0xb8bfc6, 0.3);
const CART_HANDLE = paint(0xd9534f, 0.5);

PORCELAIN_INNER.side = THREE.DoubleSide; // the open-ended basin needs both faces

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

const _matrix = new THREE.Matrix4();
const _quat = new THREE.Quaternion();
const _euler = new THREE.Euler();
const _pos = new THREE.Vector3();
const _scale = new THREE.Vector3();

interface Placement {
  rx?: number;
  ry?: number;
  rz?: number;
  sx?: number;
  sy?: number;
  sz?: number;
}

/** Bake a transform into a geometry, in place, so it can be welded. */
function at(
  geo: THREE.BufferGeometry,
  x: number,
  y: number,
  z: number,
  p: Placement = {},
): THREE.BufferGeometry {
  _euler.set(p.rx ?? 0, p.ry ?? 0, p.rz ?? 0);
  _quat.setFromEuler(_euler);
  _pos.set(x, y, z);
  _scale.set(p.sx ?? 1, p.sy ?? 1, p.sz ?? 1);
  _matrix.compose(_pos, _quat, _scale);
  return geo.applyMatrix4(_matrix);
}

/** Weld same-material parts into one geometry, releasing the inputs. */
function weld(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  if (parts.length === 1) return parts[0];
  const merged = mergeGeometries(parts, false);
  if (!merged) return parts[0]; // shouldn't happen: all parts share attributes
  parts.forEach((p) => p.dispose());
  return merged;
}

/** A welded mesh that casts and receives shadows. */
function solid(geo: THREE.BufferGeometry, material: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(geo, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

// ---------------------------------------------------------------------------
// Wheels
// ---------------------------------------------------------------------------

/**
 * A wheel is a Group so the caller can spin it about its own X axis: the tyre
 * inside is rotated to lay the cylinder on its side, the group itself is not.
 * The rim carries three spokes — without them a spinning wheel looks static —
 * welded into the hub so the whole rim is one mesh.
 */
function makeWheel(x: number, z: number, radius: number, width: number): THREE.Group {
  const wheel = new THREE.Group();
  wheel.position.set(x, radius, z);
  // Yaw (steering) must wrap the spin, not the other way round.
  wheel.rotation.order = 'YXZ';
  wheel.userData.wheel = true;

  const tyre = new THREE.CylinderGeometry(radius, radius, width, 18);
  wheel.add(solid(at(tyre, 0, 0, 0, { rz: Math.PI / 2 }), TYRE_MAT));

  const rimParts: THREE.BufferGeometry[] = [
    at(new THREE.CylinderGeometry(radius * 0.52, radius * 0.52, width * 1.06, 14), 0, 0, 0, {
      rz: Math.PI / 2,
    }),
  ];
  for (let i = 0; i < 3; i++) {
    rimParts.push(
      at(new THREE.BoxGeometry(width * 1.08, radius * 1.5, radius * 0.16), 0, 0, 0, {
        rx: (i / 3) * Math.PI,
      }),
    );
  }
  wheel.add(solid(weld(rimParts), RIM_MAT));

  return wheel;
}

/** Add four wheels resting on y = 0, marking the front pair as steered. */
function addWheels(
  group: THREE.Group,
  halfTrack: number,
  frontZ: number,
  rearZ: number,
  radius: number,
  width: number,
): void {
  for (const [x, z, steered] of [
    [-halfTrack, frontZ, 1],
    [halfTrack, frontZ, 1],
    [-halfTrack, rearZ, 0],
    [halfTrack, rearZ, 0],
  ] as const) {
    const wheel = makeWheel(x, z, radius, width);
    if (steered) wheel.userData.steered = true;
    group.add(wheel);
  }
}

/** The rear light bar every kart carries: red under braking, dark otherwise. */
function addBrakeBar(group: THREE.Group, y: number, z: number, width: number): void {
  const bar = new THREE.Mesh(new THREE.BoxGeometry(width, 0.1, 0.08), lamp(0xff2a12));
  bar.position.set(0, y, z);
  bar.userData.brake = true;
  group.add(bar);
}

/** Add a thruster nozzle whose emissive tracks the boost. */
function addThruster(
  group: THREE.Group,
  geo: THREE.BufferGeometry,
  color: number,
): void {
  const nozzle = solid(geo, lamp(color));
  nozzle.userData.glow = true;
  group.add(nozzle);
}

// ---------------------------------------------------------------------------
// Prototypes. Built once per vehicle, then cloned per kart.
// ---------------------------------------------------------------------------

interface Prototype {
  group: THREE.Group;
  /** Local-space points where boost flames emit. */
  exhausts: THREE.Vector3[];
  /** Local-space rear contact patches for tyre smoke and skid marks. */
  contacts: THREE.Vector3[];
}

function buildDuck(): Prototype {
  const g = new THREE.Group();
  const wheelR = 0.28;
  addWheels(g, 0.62, 0.62, -0.62, wheelR, 0.22);

  // Hull, tail, neck and head are all the same yellow — one mesh.
  g.add(solid(weld([
    at(new THREE.SphereGeometry(0.6, 24, 18), 0, wheelR + 0.42, -0.05, { sx: 0.95, sy: 0.78, sz: 1.35 }),
    at(new THREE.ConeGeometry(0.34, 0.55, 16), 0, wheelR + 0.62, -0.9, { rx: -Math.PI / 2.4 }),
    at(new THREE.CylinderGeometry(0.22, 0.28, 0.5, 16), 0, wheelR + 0.68, 0.55),
    at(new THREE.SphereGeometry(0.34, 20, 16), 0, wheelR + 0.98, 0.62),
  ]), DUCK_BODY));

  g.add(solid(at(new THREE.ConeGeometry(0.18, 0.42, 14), 0, wheelR + 0.92, 0.98, { rx: Math.PI / 2 }), DUCK_BEAK));

  g.add(solid(weld([-1, 1].map((side) =>
    at(new THREE.SphereGeometry(0.1, 14, 12), 0.16 * side, wheelR + 1.08, 0.82))), EYE_WHITE));
  g.add(new THREE.Mesh(weld([-1, 1].map((side) =>
    at(new THREE.SphereGeometry(0.05, 10, 8), 0.18 * side, wheelR + 1.08, 0.9))), EYE_BLACK));

  // The squeaker, repurposed as a jet nozzle.
  addThruster(g, at(new THREE.CylinderGeometry(0.15, 0.19, 0.28, 14), 0, wheelR + 0.28, -0.98, { rx: Math.PI / 2 }), 0xffb03a);
  addBrakeBar(g, wheelR + 0.55, -0.92, 0.72);

  return {
    group: g,
    exhausts: [new THREE.Vector3(0, wheelR + 0.28, -1.16)],
    contacts: [new THREE.Vector3(-0.62, 0.03, -0.62), new THREE.Vector3(0.62, 0.03, -0.62)],
  };
}

function buildChair(): Prototype {
  const g = new THREE.Group();
  const casterR = 0.16;
  const seatTop = casterR + 0.7;
  const armLen = 0.62;

  // Five-star base: central hub, five radiating arms and the gas cylinder.
  const chromeParts: THREE.BufferGeometry[] = [
    at(new THREE.CylinderGeometry(0.2, 0.22, 0.16, 14), 0, casterR + 0.18, 0),
    at(new THREE.CylinderGeometry(0.1, 0.12, 0.5, 14), 0, casterR + 0.44, 0),
  ];
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const dx = Math.sin(a);
    const dz = Math.cos(a);
    chromeParts.push(
      at(new THREE.BoxGeometry(0.12, 0.1, armLen), dx * armLen * 0.5, casterR + 0.16, dz * armLen * 0.5, { ry: a }),
    );

    // Each caster is its own Group so it can roll like the other karts' wheels.
    const caster = new THREE.Group();
    caster.rotation.order = 'YXZ';
    caster.position.set(dx * armLen, casterR, dz * armLen);
    caster.userData.wheel = true;
    if (dz > 0.3) caster.userData.steered = true; // the front pair swivels
    caster.add(solid(new THREE.SphereGeometry(casterR, 14, 12), TYRE_MAT));
    caster.add(solid(
      at(new THREE.CylinderGeometry(casterR * 1.02, casterR * 1.02, 0.05, 14), 0, 0, 0, { rz: Math.PI / 2 }),
      RIM_MAT,
    ));
    g.add(caster);
  }
  g.add(solid(weld(chromeParts), CHROME_MAT));

  g.add(solid(at(new THREE.BoxGeometry(0.95, 0.2, 0.95), 0, seatTop, 0), CHAIR_CUSHION));

  // Backrest plus both armrests share the blue frame.
  g.add(solid(weld([
    at(new THREE.BoxGeometry(0.95, 0.9, 0.16), 0, seatTop + 0.5, -0.42),
    ...[-1, 1].map((side) => at(new THREE.BoxGeometry(0.14, 0.14, 0.7), 0.48 * side, seatTop + 0.22, 0.02)),
  ]), CHAIR_FRAME));

  // The rocket strapped to the back.
  g.add(solid(at(new THREE.CylinderGeometry(0.16, 0.16, 0.6, 16), 0, seatTop + 0.3, -0.62, { rx: Math.PI / 2 }), CHAIR_ROCKET));
  g.add(solid(at(new THREE.ConeGeometry(0.16, 0.24, 16), 0, seatTop + 0.3, -0.24, { rx: Math.PI / 2 }), CHAIR_NOSE));
  addThruster(g, at(new THREE.ConeGeometry(0.15, 0.5, 14), 0, seatTop + 0.3, -1.08, { rx: -Math.PI / 2 }), 0xff7a1f);
  addBrakeBar(g, seatTop + 0.9, -0.5, 0.8);

  return {
    group: g,
    exhausts: [new THREE.Vector3(0, seatTop + 0.3, -1.3)],
    contacts: [new THREE.Vector3(-0.55, 0.03, -0.5), new THREE.Vector3(0.55, 0.03, -0.5)],
  };
}

function buildTub(): Prototype {
  const g = new THREE.Group();
  const wheelR = 0.24;
  addWheels(g, 0.6, 0.58, -0.58, wheelR, 0.2);

  // Shell, both end caps and the rim are all porcelain — one mesh.
  g.add(solid(weld([
    at(new THREE.CylinderGeometry(0.62, 0.55, 1.8, 28), 0, wheelR + 0.5, 0, { rx: Math.PI / 2, sx: 1.05 }),
    ...[0.9, -0.9].map((z) =>
      at(new THREE.SphereGeometry(0.6, 22, 18), 0, wheelR + 0.5, z, { sx: 1.05, sz: 0.6 })),
    at(new THREE.TorusGeometry(0.5, 0.06, 12, 32), 0, wheelR + 0.85, 0, { rx: Math.PI / 2, sy: 1.7 }),
  ]), PORCELAIN));

  // Hollow-look inner basin (a slightly darker inset on top).
  g.add(new THREE.Mesh(
    at(new THREE.CylinderGeometry(0.46, 0.42, 1.5, 22, 1, true), 0, wheelR + 0.72, 0, { rx: Math.PI / 2 }),
    PORCELAIN_INNER,
  ));

  // Faucet, spout and both tap handles.
  g.add(solid(weld([
    at(new THREE.CylinderGeometry(0.07, 0.09, 0.35, 14), 0, wheelR + 0.95, 0.72),
    at(new THREE.CylinderGeometry(0.05, 0.06, 0.3, 14), 0, wheelR + 1.1, 0.82, { rx: Math.PI / 3 }),
    ...[-1, 1].map((side) => at(new THREE.SphereGeometry(0.07, 12, 10), 0.18 * side, wheelR + 0.95, 0.68)),
  ]), CHROME_MAT));

  // Four ornate clawfeet.
  const feet: THREE.BufferGeometry[] = [];
  for (const fx of [-0.42, 0.42]) {
    for (const fz of [0.6, -0.6]) {
      feet.push(at(new THREE.SphereGeometry(0.16, 14, 12), fx, wheelR + 0.02, fz, { sy: 0.7, sz: 1.2 }));
    }
  }
  g.add(solid(weld(feet), BRASS));

  // The drain doubles as the thruster.
  addThruster(g, at(new THREE.CylinderGeometry(0.13, 0.16, 0.24, 14), 0, wheelR + 0.42, -1.02, { rx: Math.PI / 2 }), 0x66d9ff);
  addBrakeBar(g, wheelR + 0.78, -1.0, 0.68);

  return {
    group: g,
    exhausts: [new THREE.Vector3(0, wheelR + 0.42, -1.22)],
    contacts: [new THREE.Vector3(-0.6, 0.03, -0.58), new THREE.Vector3(0.6, 0.03, -0.58)],
  };
}

function buildCart(): Prototype {
  const g = new THREE.Group();
  const wheelR = 0.2;
  addWheels(g, 0.55, 0.6, -0.6, wheelR, 0.14);

  const basinY = wheelR + 0.35; // bottom of the basket
  const basketH = 0.75;
  const basketLen = 1.5;
  const basketW = 1.0;
  const halfW = basketW / 2;
  const frontZ = 0.75;
  const backZ = -0.75;

  // The entire wire basket — posts, rails, slats, floor, back panel and handle
  // stems — is one mesh. It used to be about thirty.
  const wire: THREE.BufferGeometry[] = [];

  // Corner posts, taller at the back to suggest the classic slope.
  for (const [px, pz, h] of [
    [-halfW, frontZ, basketH * 0.7],
    [halfW, frontZ, basketH * 0.7],
    [-halfW, backZ, basketH],
    [halfW, backZ, basketH],
  ] as const) {
    wire.push(at(new THREE.BoxGeometry(0.04, h, 0.04), px, basinY + h / 2, pz));
  }

  // Horizontal rails at two heights along every side.
  for (const rh of [0.15, 0.5]) {
    for (const side of [-halfW, halfW]) {
      wire.push(at(new THREE.BoxGeometry(0.04, 0.04, basketLen), side, basinY + rh, 0));
    }
    for (const z of [frontZ, backZ]) {
      wire.push(at(new THREE.BoxGeometry(basketW, 0.04, 0.04), 0, basinY + rh, z));
    }
  }

  // Vertical slats implying the mesh.
  for (let i = -2; i <= 2; i++) {
    const z = (i / 2) * (basketLen / 2 - 0.1);
    for (const side of [-halfW, halfW]) {
      wire.push(at(new THREE.BoxGeometry(0.04, 0.55, 0.04), side, basinY + 0.3, z));
    }
  }

  wire.push(at(new THREE.BoxGeometry(basketW, 0.05, basketLen), 0, basinY, 0));
  // Slanted back panel (the part that swings for nesting carts).
  wire.push(at(new THREE.BoxGeometry(basketW, 0.7, 0.05), 0, basinY + 0.4, backZ - 0.05, { rx: 0.25 }));
  for (const side of [-halfW, halfW]) {
    wire.push(at(new THREE.CylinderGeometry(0.045, 0.045, 0.5, 12), side, basinY + basketH - 0.15, backZ - 0.06, { rx: -0.2 }));
  }
  g.add(solid(weld(wire), WIRE));

  // Push handle arching up at the back (-Z).
  g.add(solid(
    at(new THREE.CylinderGeometry(0.05, 0.05, basketW, 14), 0, basinY + basketH + 0.1, backZ - 0.12, { rz: Math.PI / 2 }),
    CART_HANDLE,
  ));

  // A jet duct-taped under the basket, because of course.
  addThruster(g, at(new THREE.CylinderGeometry(0.11, 0.14, 0.26, 14), 0, basinY - 0.02, backZ - 0.24, { rx: Math.PI / 2 }), 0x9dff5a);
  addBrakeBar(g, basinY + 0.62, backZ - 0.14, 0.7);

  return {
    group: g,
    exhausts: [new THREE.Vector3(0, basinY - 0.02, backZ - 0.44)],
    contacts: [new THREE.Vector3(-0.55, 0.03, -0.6), new THREE.Vector3(0.55, 0.03, -0.6)],
  };
}

const BUILDERS: Record<string, () => Prototype> = {
  duck: buildDuck,
  chair: buildChair,
  tub: buildTub,
  cart: buildCart,
};

/** Prototypes are built on first use and shared by every kart of that type. */
const prototypes = new Map<string, Prototype>();

function prototypeFor(id: string): Prototype {
  let proto = prototypes.get(id);
  if (!proto) {
    proto = (BUILDERS[id] ?? buildDuck)();
    prototypes.set(id, proto);
  }
  return proto;
}

/**
 * Build a renderable kart. The group is centered in XZ, faces +Z, and rests with
 * its wheels on the y = 0 plane. Unknown ids fall back to the first vehicle.
 *
 * The clone shares geometry and static materials with the prototype; only the
 * emissive materials are copied, because each kart boosts and brakes on its own.
 */
export function createVehicle(id: string): VehicleVisual {
  const stats = getVehicle(id);
  const proto = prototypeFor(stats.id);
  const group = proto.group.clone(true);
  group.name = stats.id;

  const wheels: THREE.Object3D[] = [];
  const steered: THREE.Object3D[] = [];
  const glow: THREE.MeshStandardMaterial[] = [];
  const brakeLights: THREE.MeshStandardMaterial[] = [];

  group.traverse((obj) => {
    if (obj.userData.wheel) wheels.push(obj);
    if (obj.userData.steered) steered.push(obj);
    if (!obj.userData.glow && !obj.userData.brake) return;
    const mesh = obj as THREE.Mesh;
    const material = (mesh.material as THREE.MeshStandardMaterial).clone();
    mesh.material = material;
    (obj.userData.glow ? glow : brakeLights).push(material);
  });

  // Anchors are read-only for callers, so the prototype's copies are shared.
  return { group, wheels, steered, exhausts: proto.exhausts, contacts: proto.contacts, glow, brakeLights };
}

/**
 * Release the per-kart resources `createVehicle` allocated. Geometry and static
 * materials belong to the shared prototype and deliberately survive.
 */
export function disposeVehicle(visual: VehicleVisual): void {
  for (const material of visual.glow) material.dispose();
  for (const material of visual.brakeLights) material.dispose();
}
